import { buildExportRequest } from '../utils/exportSelection';
// src/context/AppProvider.tsx - ENHANCED: Billing integration with insufficient balance modal
import { useReducer, useCallback, useRef, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { AppContext, appReducer, initialState } from './AppContext';
import { useJobPolling } from '../hooks/useJobPolling';
import type {
  AppContextType,
  ToastMessage,
  ToastDetailMetrics,
  ConfirmationDialog,
  SortingState,
  FilterValue,
  DataSourceType,
  SearchResponse,
  CreateJobResponse,
  NavigationState,
  Job
} from '../types';
import { generateId } from '../utils';
import { API_BASE_URL } from '../constants';
import { useAuth } from '../hooks/useAuth';
import InsufficientBalanceModal from '../components/InsufficientBalanceModal';

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { apiKey } = useAuth();
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    dispatch({ type: 'CLEAR_SEARCH_RESULTS' });
  }, [apiKey]);

  // Load search filters when user is authenticated and app initializes
  useEffect(() => {
    if (apiKey && state.initialized && state.navigationState.view === 'people') {
      // Load saved search filters for authenticated user
      try {
        const key = 'search';
        const data = JSON.parse(localStorage.getItem('ppl_contact_filters') || '{}');
        const saved = data[key];
        if (saved && Date.now() - saved.timestamp < 24 * 60 * 60 * 1000) {
          if (Object.keys(saved.filters || {}).length > 0 || saved.query) {
            dispatch({ type: 'SET_FILTERS', payload: saved.filters || {} });
            if (saved.query) {
              dispatch({ type: 'SET_SEARCH_QUERY', payload: saved.query });
            }
          }
        }
      } catch (error) {
        console.warn('Failed to load saved search filters:', error);
      }
    }
  }, [apiKey, state.initialized, state.navigationState.view]);


  // State for insufficient balance modal
  const [insufficientBalanceModal, setInsufficientBalanceModal] = useState({
    isOpen: false,
    currentBalance: 0,
    requiredAmount: 0
  });

  // Track recent toasts to prevent duplicates
  const recentToastsRef = useRef<Map<string, number>>(new Map());

  const showToast = useCallback((toast: Omit<ToastMessage, 'id' | 'timestamp'>) => {
    // Create a key based on toast content to detect duplicates
    const toastKey = `${toast.type}-${toast.title}-${toast.message}`;
    const now = Date.now();
    const recentToasts = recentToastsRef.current;
    
    // Check if the same toast was shown recently (within 2 seconds)
    if (recentToasts.has(toastKey)) {
      const lastShown = recentToasts.get(toastKey)!;
      if (now - lastShown < 2000) {

        return; // Don't show duplicate toast
      }
    }
    
    // Record this toast and clean up old entries
    recentToasts.set(toastKey, now);
    
    // Clean up entries older than 5 seconds
    for (const [key, timestamp] of recentToasts.entries()) {
      if (now - timestamp > 5000) {
        recentToasts.delete(key);
      }
    }
    
    const toastMessage: ToastMessage = { ...toast, id: generateId(), timestamp: Date.now() };
    dispatch({ type: 'ADD_TOAST', payload: toastMessage });
  }, []);

  const showConfirmationDialog = useCallback((dialog: Omit<ConfirmationDialog, 'isOpen'>) => {
    dispatch({ type: 'SHOW_CONFIRMATION_DIALOG', payload: { ...dialog, isOpen: true } });
  }, []);

  const hideConfirmationDialog = useCallback(() => {
    dispatch({ type: 'HIDE_CONFIRMATION_DIALOG' });
  }, []);

  const getCompleteFilters = useCallback((): Record<string, unknown> => {
    const currentState = stateRef.current;
    let filters = { ...currentState.searchState.filters };

    if (currentState.navigationState.view === 'list' && currentState.navigationState.listId) {
      filters = {
        ...filters,
        list_ids: { type: 'picklist', values: [currentState.navigationState.listId], operator: 'OR' }
      };
    }

    return filters;
  }, []);

  const getVisibleUUIDs = useCallback((): string[] => {
    const currentState = stateRef.current;
    return currentState.contacts
      .map(contact => contact.UUID)
      .filter((uuid): uuid is string => Boolean(uuid));
  }, []);


  const getAllMatchingCompanyIds = useCallback(async (): Promise<string[]> => {
    const currentState = stateRef.current;
    const filters = getCompleteFilters();
    
    const searchParams = {
      query: currentState.searchState.query || '*', // Use wildcard if no query
      filters: filters,
      data_source: 'company',
      page: 1,
      page_size: currentState.totalResults, // Get all results
      sorting: currentState.searchState.sorting
    };
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(searchParams)
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const result = await response.json();
      
      // Handle different response structures - use 'hits' for the actual data
      const contacts = result.hits || result.contacts || result.data?.contacts || result.data || [];
      return contacts
        .map((contact: any) => contact.COMPANY_ID)
        .filter((id: string): id is string => Boolean(id));
    } catch (error) {
      console.error('Failed to get all matching company IDs:', error);
      return [];
    }
  }, [apiKey, getCompleteFilters]);

  const refreshCurrentView = useCallback(async () => {
    try {


      const currentState = stateRef.current;
      const { searchState, pageSize, schema } = currentState;

      const completeFilters = getCompleteFilters();

      const apiSearchState: Record<string, unknown> = {
        query: "",
        page: searchState.page,
        size: pageSize,
        filters: Object.keys(completeFilters).length > 0 ? completeFilters : undefined,
        data_source: searchState.dataSource,
      };

      // Add field grouping parameters if they exist
      if (completeFilters.group_contact_keyword_fields !== undefined) {
        apiSearchState.group_contact_keyword_fields = completeFilters.group_contact_keyword_fields;
        delete completeFilters.group_contact_keyword_fields; // Remove from filters
      }
      if (completeFilters.group_company_keyword_fields !== undefined) {
        apiSearchState.group_company_keyword_fields = completeFilters.group_company_keyword_fields;
        delete completeFilters.group_company_keyword_fields; // Remove from filters
      }

      if (searchState.sorting && searchState.sorting.length > 0 && schema) {
        const columnId = searchState.sorting[0].id;
        const columnConfig = schema.column_configs?.find(col => col.id === columnId);
        if (columnConfig) {
          apiSearchState.sort_field = columnConfig.sort_field;
          apiSearchState.sort_order = searchState.sorting[0].desc ? 'desc' : 'asc';
        }
      }



      const response = await fetch(`${API_BASE_URL}/api/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
        body: JSON.stringify(apiSearchState)
      });

      if (!response.ok) {
        if (response.status === 401) {

          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return;
        }
        const errorData = await response.json();
        if (response.status === 402 && errorData.code === 'search_preview_limit') {
          showToast({ type: 'warning', title: 'Search Preview Limit', message: errorData.detail });
          dispatch({ type: 'SET_PAGE', payload: 1 });
          return;
        }
        throw new Error(errorData.detail || `API request failed: ${response.status} ${response.statusText}`);
      }

      const result: SearchResponse = await response.json();

      if (result) {
        dispatch({
          type: 'SET_SEARCH_RESULTS',
          payload: {
            contacts: result.hits,
            total: result.total,
            totalPages: result.total_pages,
            page: result.page,
            searchTime: result.query_time_ms,
            dataSource: searchState.dataSource,
            searchAccess: result.search_access
          }
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Could not refresh the view.';
      console.error('❌ Failed to refresh current view:', error);
      showToast({
        type: 'error',
        title: 'Refresh Failed',
        message: errorMessage
      });
    }
  }, [apiKey, showToast, getCompleteFilters]);

  const onJobsUpdate = useCallback((updatedJobs: Job[]) => {

    dispatch({ type: 'SET_ACTIVE_JOBS', payload: updatedJobs });
  }, []);

  // ✅ FIXED: Bulletproof job completion handler with better logging
  const onJobComplete = useCallback((completedJob: Job, dismiss: () => void) => {


    const currentState = stateRef.current;
    const isContactsOperation = ['add_to_list', 'delete_contacts', 'remove_from_list'].includes(completedJob.job_type);
    const isAccountsOperation = ['add_to_account_list', 'delete_accounts', 'remove_from_account_list'].includes(completedJob.job_type);
    const isPeopleOperation = completedJob.job_type === 'contacts_unlock';
    const currentDataSource = currentState.searchState.dataSource;

    // ✅ CRITICAL FIX: Clear processing state for ANY completed job that might have processing UUIDs
    const isFinalStatus = ['completed', 'failed', 'canceled'].includes(completedJob.status);
    if (isFinalStatus && currentState.processing.uuids.size > 0) {

      // ✅ ENHANCED: More forgiving matching logic
      const shouldClearProcessing = (
        // Direct job ID match
        currentState.processing.jobId === completedJob.job_id ||
        // Pending job ID (for quick completions)
        currentState.processing.jobId === 'pending' ||
        // Operation type match
        (currentState.processing.operation === 'delete' && ['delete_contacts', 'delete_accounts', 'remove_from_list', 'remove_from_account_list'].includes(completedJob.job_type))
      );

      if (shouldClearProcessing) {

        dispatch({ type: 'CLEAR_PROCESSING' });
      } else {

      }
    }

    // Refresh view for contacts operations
    if (isContactsOperation && currentDataSource === 'contacts') {

      setTimeout(() => refreshCurrentView(), 500);
    } else if (isAccountsOperation && currentDataSource === 'accounts') {
      // ✅ REFRESH: Accounts view refreshed after job completion
      setTimeout(() => refreshCurrentView(), 500);
    } else if (isPeopleOperation) {

      setTimeout(() => {
        const event = new Event('lists-refresh');
        window.dispatchEvent(event);
      }, 500);
    }

    // Better status-specific messaging
    if (completedJob.status === 'failed') {
      showToast({
        type: 'error',
        title: 'Operation Failed',
        message: completedJob.error || 'The operation failed to complete',
        duration: 7000
      });
    } else if (completedJob.status === 'completed') {
      // Show toast for operations that need billing information (account unlock jobs show in ProgressTracker at bottom)
      if (completedJob.result?.billing?.job_cost || completedJob.result?.billing?.amount_spent) {
        let message: string | ToastDetailMetrics = 'Operation completed successfully';
        let cost = null;
        
        // Add count information for specific job types
        if (completedJob.job_type === 'delete_contacts' && completedJob.result?.deleted_count) {
          const count = completedJob.result.deleted_count;
          message = `${count.toLocaleString()} contact${count !== 1 ? 's' : ''} deleted successfully`;
        } else if (completedJob.job_type === 'contacts_unlock') {
          const netNew = completedJob.result?.net_new_contacts || 0;
          const refresh = completedJob.result?.refresh_contacts || 0;
          const total = netNew + refresh;
          
          if (total > 0) {
            message = {
              total,
              netNew: netNew > 0 ? netNew : undefined,
              refresh: refresh > 0 ? refresh : undefined,
              label: 'contacts unlocked'
            };
          } else {
            message = 'No contacts needed unlocking';
          }
        }
        
        if (completedJob.result?.billing?.job_cost) {
          cost = parseFloat(String(completedJob.result.billing.job_cost));
        } else if (completedJob.result?.billing?.amount_spent) {
          cost = parseFloat(String(completedJob.result.billing.amount_spent));
        }
        
        if (cost !== null && typeof message === 'string') {
          message += ` • Cost: $${cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
        
        showToast({
          type: 'success',
          title: 'Operation Completed',
          message: message,
          duration: 5000
        });
      }
    }

    // Faster dismiss for cancelled jobs
    const dismissDelay = completedJob.status === 'canceled' ? 1500 : 4500;
    setTimeout(() => dismiss(), dismissDelay);
  }, [refreshCurrentView, showToast]);

  const { addJobId, cancelJob, dismissJob } = useJobPolling({
    apiKey: apiKey || '',
    onJobsUpdate,
    onJobComplete,
  });

  const removeJob = useCallback((jobId: string) => {
    dismissJob(jobId);
  }, [dismissJob]);

  // ✅ ENHANCED: Better job request with immediate state tracking
  const makeJobRequest = useCallback(async (
    endpoint: string,
    requestBody: Record<string, unknown>,
    jobType: Job['job_type'],
    selectAllOverride?: boolean
  ): Promise<string | null> => {
    const currentState = stateRef.current;
    let processingUuids: string[] = [];

    // Determine which UUIDs to track
    if (['delete_contacts', 'remove_from_list', 'contacts_unlock'].includes(jobType)) {
      const useSelectAll = selectAllOverride !== undefined ? selectAllOverride : currentState.selection.selectAll;
      if (useSelectAll) {
        processingUuids = getVisibleUUIDs();
      } else {
        processingUuids = Array.from(currentState.selection.selectedIds);
      }
    }

    // ✅ FIXED: Clear ANY existing processing state first
    if (currentState.processing.uuids.size > 0) {
      dispatch({ type: 'CLEAR_PROCESSING' });
    }

    // Set temporary processing state with a placeholder job ID
    if (processingUuids.length > 0) {
      let operation: 'delete' | null = null;

      if (['delete_contacts', 'remove_from_list'].includes(jobType)) {
        operation = 'delete';
      }

      if (operation) {
        const tempJobId = `pending-${Date.now()}`;

        dispatch({
          type: 'SET_PROCESSING',
          payload: {
            uuids: processingUuids,
            operation,
            jobId: tempJobId
          }
        });
      }
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Clear processing state on error

        dispatch({ type: 'CLEAR_PROCESSING' });
        throw new Error(errorData.detail || `Job creation failed: ${response.status}`);
      }

      const result: CreateJobResponse = await response.json();

      // Add job to polling system

      addJobId(result.job_id, jobType);

      // ✅ FIXED: Update processing state with real job ID immediately
      if (processingUuids.length > 0 && stateRef.current.processing.uuids.size > 0) {
        let operation: 'delete' | null = null;

        if (['delete_contacts', 'remove_from_list'].includes(jobType)) {
          operation = 'delete';
        }

        if (operation) {

          dispatch({
            type: 'SET_PROCESSING',
            payload: {
              uuids: processingUuids,
              operation,
              jobId: result.job_id
            }
          });
        }
      }

      // Clear selection after setting processing state
      dispatch({ type: 'CLEAR_SELECTION' });

      return result.job_id;
    } catch (error) {
      console.error(`❌ ${jobType} job failed:`, error);
      // Always clear processing state on any error
      dispatch({ type: 'CLEAR_PROCESSING' });
      throw error;
    }
  }, [apiKey, addJobId, getVisibleUUIDs, dispatch]);

  // ENHANCED: unlockSelectedContacts with pay-as-you-go support
  const unlockSelectedContacts = useCallback(async (owner_id: string, list_ids: string[], selectAll: boolean, limit?: number) => {
    try {
      // First, check if pay-as-you-go is available
      const payAsYouGoStatus = await fetch(`${API_BASE_URL}/api/pay-as-you-go/status`, {
        headers: { 'Authorization': `Bearer ${owner_id}` }
      }).then(res => res.ok ? res.json() : null).catch(() => null);

      if (payAsYouGoStatus?.success && payAsYouGoStatus.can_use_pay_as_you_go) {
        // Use pay-as-you-go billing
        const selectedCount = selectAll ? state.totalResults : state.selection.selectedIds.size;
        const filters = selectAll ? getCompleteFilters() : undefined;
        const person_uuids = selectAll ? undefined : Array.from(state.selection.selectedIds);

        const requestBody: Record<string, unknown> = { limit: limit || selectedCount, list_ids };
        if (person_uuids && person_uuids.length > 0) {
          requestBody.person_uuids = person_uuids;
        } else if (filters && Object.keys(filters).length > 0) {
          requestBody.filters = filters;
        } else {
          throw new Error('Must select contacts or have active filters');
        }

        // ✅ FIXED: Set processing state for pay-as-you-go operations
        const currentState = stateRef.current;
        let processingUuids: string[] = [];

        // Determine which UUIDs to track
        if (selectAll) {
          processingUuids = getVisibleUUIDs();
        } else {
          processingUuids = Array.from(currentState.selection.selectedIds);
        }

        // Clear any existing processing state first
        if (currentState.processing.uuids.size > 0) {

          dispatch({ type: 'CLEAR_PROCESSING' });
        }

        // Set processing state for pay-as-you-go operation
        if (processingUuids.length > 0) {
          const tempJobId = `pending_${Date.now()}`;

          dispatch({
            type: 'SET_PROCESSING',
            payload: {
              uuids: processingUuids,
              operation: null,
              jobId: tempJobId
            }
          });
        }

        // Use pay-as-you-go endpoint
        const response = await fetch(`${API_BASE_URL}/api/pay-as-you-go/unlock-contacts`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${owner_id}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          if (response.status === 401) {

            localStorage.setItem('show_login_page', 'true');
            window.location.reload();
            return;
          }
          const errorData = await response.json().catch(() => ({}));
          // Clear processing state on error

          dispatch({ type: 'CLEAR_PROCESSING' });
          throw new Error(errorData.detail || `Pay-as-you-go unlock failed: ${response.status}`);
        }

        const result = await response.json();

        
        // Pay-as-you-go creates a job, so we should track it
        if (result.job_id) {

          addJobId(result.job_id, 'contacts_unlock');

          
          // Update processing state with real job ID
          if (processingUuids.length > 0 && stateRef.current.processing.uuids.size > 0) {

            dispatch({
              type: 'SET_PROCESSING',
              payload: {
                uuids: processingUuids,
                operation: null,
                jobId: result.job_id
              }
            });
          }
          
          // Clear selection after setting processing state
          dispatch({ type: 'CLEAR_SELECTION' });
        } else {
          // Fallback for immediate completion (shouldn't happen with current backend)
          const message = result.debt_created 
            ? `Unlocked ${result.contacts_unlocked} contacts. Debt created: $${result.debt_created}`
            : `Unlocked ${result.contacts_unlocked} contacts. Charged: $${result.total_charged}`;
          
          showToast({ 
            type: 'success', 
            title: 'Contacts Unlocked', 
            message 
          });

          // Clear processing state after successful operation

          dispatch({ type: 'CLEAR_PROCESSING' });
          
          // Clear selection after successful operation
          dispatch({ type: 'CLEAR_SELECTION' });
        }
        
      } else {
        // Fall back to regular job-based billing
        const currentBalance = state.billingBalance;
        if (currentBalance && typeof currentBalance.balance === 'number') {
          const selectedCount = selectAll ? state.totalResults : state.selection.selectedIds.size;
          const pricePerContact = parseFloat(state.schema?.ui_config?.billing?.price_per_contact || '0.02');
          const estimatedCost = selectedCount * pricePerContact;

          // Check if balance is sufficient (allowing -$10.00 max debt)
          if (currentBalance.balance - estimatedCost < -10.00) {
            setInsufficientBalanceModal({
              isOpen: true,
              currentBalance: currentBalance.balance,
              requiredAmount: estimatedCost
            });
            return;
          }
        }

        const filters = selectAll ? getCompleteFilters() : undefined;
        const person_uuids = selectAll ? undefined : Array.from(state.selection.selectedIds);

        const requestBody: Record<string, unknown> = {};
        if (limit) {
          requestBody.limit = limit;
        }
        
        if (person_uuids && person_uuids.length > 0) {
          // Individual selections - include list_ids if provided
          requestBody.person_uuids = person_uuids;
          if (list_ids && list_ids.length > 0) {
            requestBody.list_ids = list_ids;
          }
        } else if (filters && Object.keys(filters).length > 0) {
          // Select all with filters - include list_ids if provided
          requestBody.filters = filters;
          if (list_ids && list_ids.length > 0) {
            requestBody.list_ids = list_ids;
          }
        } else if (selectAll) {
          // Select all without filters - use list_ids
          requestBody.list_ids = list_ids;
        } else {
          throw new Error('Must select contacts or have active filters');
        }

        await makeJobRequest(`${API_BASE_URL}/api/contacts/unlock-jobs`, requestBody, 'contacts_unlock', selectAll);
      }

    } catch (error) {
      // Clear processing state on any error

      dispatch({ type: 'CLEAR_PROCESSING' });
      
      showToast({
        type: 'error',
        title: 'Unlock Failed',
        message: error instanceof Error ? error.message : 'Failed to start unlock job'
      });
    }
  }, [state.totalResults, state.selection.selectedIds, state.billingBalance, getCompleteFilters, makeJobRequest, showToast, getVisibleUUIDs, dispatch, addJobId]);

  // FREE Account Unlock functionality
  const unlockSelectedAccounts = useCallback(async (list_ids: string[], selectAll: boolean, limit?: number) => {
    try {
      dispatch({ type: 'SET_PROCESSING', payload: { uuids: [], operation: null, jobId: '' } });
      
      const selectedCount = selectAll ? (limit || state.totalResults) : state.selection.selectedIds.size;
      
      
      // For selectAll, we need to check if we have totalResults > 0
      if (selectAll && state.totalResults === 0) {
        showToast({
          type: 'error',
          title: 'No Results',
          message: 'No companies found to unlock. Please check your search criteria.'
        });
        return;
      }
      
      if (!selectAll && selectedCount === 0) {
        showToast({
          type: 'error',
          title: 'No Selection',
          message: 'Please select companies to unlock'
        });
        return;
      }

      const filters = selectAll ? getCompleteFilters() : undefined;
      const company_ids = selectAll ? undefined : Array.from(state.selection.selectedIds);


      if (!selectAll && (!company_ids || company_ids.length === 0)) {
        showToast({
          type: 'error',
          title: 'No Selection',
          message: 'Please select companies to unlock'
        });
        return;
      }

      if (selectAll && (!filters || Object.keys(filters).length === 0)) {
        showToast({
          type: 'error',
          title: 'No Filters',
          message: 'Please apply filters to unlock all companies'
        });
        return;
      }

      // Call the FREE account unlock API
      const requestBody: Record<string, unknown> = { list_ids };
      if (limit) {
        requestBody.limit = limit;
      }
      
      if (company_ids && company_ids.length > 0) {
        // Individual selections
        requestBody.company_ids = company_ids;
      } else if (filters && Object.keys(filters).length > 0) {
        // Select all with filters
        requestBody.filters = filters;
      } else {
        throw new Error('Must select companies or have active filters');
      }

      const response = await fetch(`${API_BASE_URL}/api/accounts/unlock-jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.setItem('show_login_page', 'true');
          window.location.reload();
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to start unlock job: ${response.status}`);
      }

      const result = await response.json();
      
      if (result && typeof result === 'object' && 'job_id' in result && typeof result.job_id === 'string') {
        addJobId(result.job_id, 'accounts_unlock');
        showToast({
          type: 'success',
          title: 'Unlock Job Created',
          message: `Unlock job created for ${selectedCount} compan${selectedCount !== 1 ? 'ies' : 'y'}.`
        });
      } else {
        throw new Error('Failed to start unlock job');
      }

    } catch (error) {
      dispatch({ type: 'CLEAR_PROCESSING' });
      showToast({
        type: 'error',
        title: 'Unlock Failed',
        message: error instanceof Error ? error.message : 'Failed to start unlock job'
      });
    }
  }, [state.totalResults, state.selection.selectedIds, getAllMatchingCompanyIds, showToast, dispatch, addJobId, apiKey]);

  const addSelectedContactsToList = useCallback(async (_owner_id: string, list_ids: string[], _selectAll: boolean, limit?: number) => {
    try {
      const contact_uuids = Array.from(state.selection.selectedIds);

      if (contact_uuids.length === 0) {
        throw new Error('No contacts selected');
      }

      const requestBody: Record<string, unknown> = { contact_uuids, list_ids };
      if (limit) {
        requestBody.limit = limit;
      }

      await makeJobRequest(`${API_BASE_URL}/api/lists/contacts:add`, requestBody, 'add_to_list');

    } catch (error) {
      showToast({
        type: 'error',
        title: 'Add to List Failed',
        message: error instanceof Error ? error.message : 'Failed to start add to list job'
      });
    }
  }, [makeJobRequest, showToast, state.selection.selectedIds]);

  const addSelectedAccountsToList = useCallback(async (list_id: string) => {
    try {
      const company_ids = state.selection.selectAll ? 
        await getAllMatchingCompanyIds() : 
        Array.from(state.selection.selectedIds);

      if (!company_ids || company_ids.length === 0) {
        showToast({
          type: 'error',
          title: 'No Selection',
          message: 'Please select companies to add to list'
        });
        return;
      }

      // Check current data source to determine which API to call
      const currentDataSource = stateRef.current.searchState.dataSource;
      
      if (currentDataSource === 'company') {
        // Company search page - unlock companies and add to list
        const response = await fetch(`${API_BASE_URL}/api/accounts/unlock-jobs`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ company_ids: company_ids, list_ids: [list_id] })
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.setItem('show_login_page', 'true');
            window.location.reload();
            return;
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to start unlock job: ${response.status}`);
        }

        const result = await response.json();
        
        if (result && typeof result === 'object' && 'job_id' in result && typeof result.job_id === 'string') {
          addJobId(result.job_id, 'accounts_unlock');
          showToast({
            type: 'success',
            title: 'Unlock Job Created',
            message: `Unlock job created for ${company_ids.length} compan${company_ids.length !== 1 ? 'ies' : 'y'}.`
          });
        } else {
          throw new Error('Failed to start unlock job');
        }
      } else {
        // Accounts list page - just add existing accounts to list
        const response = await fetch(`${API_BASE_URL}/api/account-lists/${list_id}/add-accounts`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ company_ids: company_ids })
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.setItem('show_login_page', 'true');
            window.location.reload();
            return;
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to add accounts to list: ${response.status}`);
        }

        const result = await response.json();
        
        if (result && typeof result === 'object' && result.success) {
          showToast({
            type: 'success',
            title: 'Added to List',
            message: `Added ${result.added_count || company_ids.length} account${(result.added_count || company_ids.length) !== 1 ? 's' : ''} to list.`
          });
          // Refresh the current view to show updated data
          setTimeout(() => refreshCurrentView(), 500);
        } else {
          throw new Error('Failed to add accounts to list');
        }
      }

    } catch (error) {
      showToast({
        type: 'error',
        title: 'Add to List Failed',
        message: error instanceof Error ? error.message : 'Failed to add accounts to list'
      });
    }
  }, [state.selection.selectAll, state.selection.selectedIds, getAllMatchingCompanyIds, showToast, apiKey, addJobId, refreshCurrentView]);

  const deleteContactsJobAsync = useCallback(async () => {
    showConfirmationDialog({
      title: 'Delete Contacts',
      message: `Are you sure you want to permanently delete ${state.selection.selectAll ? 'all filtered' : state.selection.selectedIds.size} contact${state.selection.selectAll || state.selection.selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.`,
      confirmText: 'Delete Contacts',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const filters = state.selection.selectAll ? getCompleteFilters() : undefined;
          const contact_uuids = state.selection.selectAll ? undefined : Array.from(state.selection.selectedIds);

          const requestBody: Record<string, unknown> = {};
          if (contact_uuids && contact_uuids.length > 0) {
            requestBody.contact_uuids = contact_uuids;
          } else if (filters !== undefined) {
            requestBody.filters = filters;
            requestBody.select_all = true;
          } else {
            throw new Error('Must select contacts or have active filters');
          }

          await makeJobRequest(`${API_BASE_URL}/api/contacts/delete/job`, requestBody, 'delete_contacts');

        } catch (error) {
          showToast({
            type: 'error',
            title: 'Delete Failed',
            message: error instanceof Error ? error.message : 'Failed to start delete job'
          });
        }
        hideConfirmationDialog();
      },
      onCancel: hideConfirmationDialog
    });
  }, [makeJobRequest, showToast, showConfirmationDialog, hideConfirmationDialog, state.selection, getCompleteFilters]);

  const removeFromListJobAsync = useCallback(async (list_id: string) => {
    try {
      const requestBody: Record<string, unknown> = { list_id };

      if (state.selection.selectAll) {
        const filters = getCompleteFilters();
        requestBody.filters = filters;
        requestBody.select_all = true;
      } else {
        requestBody.contact_uuids = Array.from(state.selection.selectedIds);
      }

      if (!requestBody.contact_uuids && !requestBody.select_all) {
        throw new Error('Must select contacts or have active filters');
      }

      await makeJobRequest(`${API_BASE_URL}/api/lists/remove/job`, requestBody, 'remove_from_list');

    } catch (error) {
      showToast({
        type: 'error',
        title: 'Remove Failed',
        message: error instanceof Error ? error.message : 'Failed to start remove job'
      });
    }
  }, [makeJobRequest, showToast, state.selection, getCompleteFilters]);

  const exportSelectedContacts = useCallback(async (_owner_id: string, format: 'json' | 'csv' = 'csv', exportFields?: string[]) => {
    try {
      const requestBody = buildExportRequest(
        state.searchState.dataSource, format, state.selection.selectAll,
        Array.from(state.selection.selectedIds), state.searchState.query,
        state.selection.selectAll ? getCompleteFilters() : undefined, exportFields
      );

      await makeJobRequest(`${API_BASE_URL}/api/export/job`, requestBody, 'export');

    } catch (error) {
      showToast({
        type: 'error',
        title: 'Export Failed',
        message: error instanceof Error ? error.message : 'Failed to start export job'
      });
    }
  }, [makeJobRequest, showToast, state.selection, state.searchState.dataSource, state.searchState.query, getCompleteFilters]);

  const deleteList = useCallback(async (_owner_id: string, list_id: string, list_name: string, onSuccess?: () => void) => {
    showConfirmationDialog({
      title: 'Delete List',
      message: `Are you sure you want to delete the list "${list_name}"? This action cannot be undone.`,
      confirmText: 'Delete List',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/lists/delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
            body: JSON.stringify({ list_id })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to delete list');
          }

          showToast({
            type: 'success',
            title: 'List Deleted',
            message: `List "${list_name}" has been deleted`
          });

          if (onSuccess) onSuccess();
        } catch (error) {
          showToast({
            type: 'error',
            title: 'Delete Failed',
            message: error instanceof Error ? error.message : 'Failed to delete list'
          });
        }
        hideConfirmationDialog();
      },
      onCancel: hideConfirmationDialog
    });
  }, [apiKey, showToast, showConfirmationDialog, hideConfirmationDialog]);

  const removeFromAccountListJobAsync = useCallback(async (list_id: string) => {
    try {
      const requestBody: Record<string, unknown> = { list_id };

      if (state.selection.selectAll) {
        const filters = getCompleteFilters();
        requestBody.filters = filters;
        requestBody.select_all = true;
      } else {
        requestBody.company_ids = Array.from(state.selection.selectedIds);
      }

      if (!requestBody.company_ids && !requestBody.select_all) {
        throw new Error('Must select accounts or have active filters');
      }

      await makeJobRequest(`${API_BASE_URL}/api/account-lists/remove/job`, requestBody, 'remove_from_account_list');

    } catch (error) {
      showToast({
        type: 'error',
        title: 'Remove Failed',
        message: error instanceof Error ? error.message : 'Failed to start remove job'
      });
    }
  }, [makeJobRequest, showToast, state.selection, getCompleteFilters]);

  const deleteAccountsJobAsync = useCallback(async () => {
    showConfirmationDialog({
      title: 'Delete Accounts',
      message: `Are you sure you want to permanently delete ${state.selection.selectAll ? 'all selected' : state.selection.selectedIds.size} account${state.selection.selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.`,
      confirmText: 'Delete Accounts',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const requestBody: Record<string, unknown> = {};

          if (state.selection.selectAll) {
            // For accounts list view, pass listId directly instead of using filters
            if (state.navigationState.view === 'list' && state.navigationState.listId) {
              requestBody.list_id = state.navigationState.listId;
              requestBody.select_all = true;
            } else {
              // For search view or when no list is selected, use filters
              const filters = getCompleteFilters();
              requestBody.filters = filters;
              requestBody.select_all = true;
            }
          } else {
            requestBody.company_ids = Array.from(state.selection.selectedIds);
          }

          if (!requestBody.company_ids && !requestBody.select_all) {
            throw new Error('Must select accounts or have active filters');
          }

          await makeJobRequest(`${API_BASE_URL}/api/accounts/delete/job`, requestBody, 'delete_accounts');

        } catch (error) {
          showToast({
            type: 'error',
            title: 'Delete Failed',
            message: error instanceof Error ? error.message : 'Failed to start delete job'
          });
        }
        hideConfirmationDialog();
      },
      onCancel: hideConfirmationDialog
    });
  }, [makeJobRequest, showToast, showConfirmationDialog, hideConfirmationDialog, state.selection, getCompleteFilters, state.navigationState]);

  const deleteAccountList = useCallback(async (_owner_id: string, list_id: string, list_name: string, onSuccess?: () => void) => {
    showConfirmationDialog({
      title: 'Delete Account List',
      message: `Are you sure you want to delete the account list "${list_name}"? This action cannot be undone.`,
      confirmText: 'Delete List',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/account-lists/${list_id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` }
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to delete account list');
          }

          showToast({
            type: 'success',
            title: 'Account List Deleted',
            message: `Account list "${list_name}" has been deleted`
          });

          if (onSuccess) onSuccess();
        } catch (error) {
          showToast({
            type: 'error',
            title: 'Delete Failed',
            message: error instanceof Error ? error.message : 'Failed to delete account list'
          });
        }
        hideConfirmationDialog();
      },
      onCancel: hideConfirmationDialog
    });
  }, [apiKey, showToast, showConfirmationDialog, hideConfirmationDialog]);

  // State setters
  const setSearchQuery = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  }, []);

  const setPage = useCallback((page: number) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  }, []);

  const setSorting = useCallback((sorting: SortingState) => {
    dispatch({ type: 'SET_SORTING', payload: Array.isArray(sorting) ? sorting : [] });
  }, []);

  const updateFilter = useCallback((field: string, value: FilterValue) => {
    const currentFilters = { ...stateRef.current.searchState.filters };
    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      delete currentFilters[field];
    } else {
      currentFilters[field] = value;
    }
    dispatch({ type: 'SET_FILTERS', payload: currentFilters });
  }, []);

  const clearAllFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_FILTERS' });
  }, []);

  const clearFiltersByCategory = useCallback((category: string) => {
    dispatch({ type: 'CLEAR_FILTERS_BY_CATEGORY', payload: { category } });
  }, []);

  const setColumnVisibility = useCallback((columns: string[]) => {
    dispatch({ type: 'SET_VISIBLE_COLUMNS', payload: columns });
  }, []);

  const setNavigation = useCallback((navigation: NavigationState) => {
    dispatch({ type: 'SET_NAVIGATION', payload: navigation });
  }, []);

  const setDataSource = useCallback((dataSource: DataSourceType) => {
    // Always set view to main when changing data source
    dispatch({ type: 'SET_VIEW', payload: 'main' });
    
    // Trigger schema reload for the new data source - visible columns will be updated when schema loads
    // SET_DATA_SOURCE will handle setting the appropriate navigation state
    dispatch({ type: 'SET_DATA_SOURCE', payload: dataSource });
  }, [dispatch]);

  const selectItem = useCallback((id: string) => { dispatch({ type: 'SELECT_ITEM', payload: id }); }, []);
  const deselectItem = useCallback((id: string) => { dispatch({ type: 'DESELECT_ITEM', payload: id }); }, []);
  const selectAll = useCallback((select: boolean) => { 
    dispatch({ type: 'SELECT_ALL', payload: select }); 
  }, []);
  const clearSelection = useCallback(() => { dispatch({ type: 'CLEAR_SELECTION' }); }, []);
  const setView = useCallback((view: 'main' | 'terms' | 'privacy' | 'activity' | 'admin' | 'billing' | 'enrichment') => { 
    dispatch({ type: 'SET_VIEW', payload: view }); 
  }, []);

  const contextValue: AppContextType = {
    state,
    dispatch,
    showToast,
    showConfirmationDialog,
    hideConfirmationDialog,
    setSearchQuery,
    setPage,
    setSorting,
    updateFilter,
    clearAllFilters,
    clearFiltersByCategory,
    setColumnVisibility,
    setView,
    setDataSource,
    setNavigation,
    selectItem,
    deselectItem,
    selectAll,
    clearSelection,
    saveSelectedContacts: async () => { },
    unlockSelectedContacts,
    unlockSelectedAccounts,
    addSelectedContactsToList,
    addSelectedAccountsToList,
    removeFromListJobAsync,
    deleteContactsJobAsync,
    removeFromAccountListJobAsync,
    deleteAccountsJobAsync,
    deleteList,
    deleteAccountList,
    exportSelectedContacts,
    addJobId,
    cancelJob,
    removeJob,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
      <InsufficientBalanceModal
        isOpen={insufficientBalanceModal.isOpen}
        onClose={() => setInsufficientBalanceModal(prev => ({ ...prev, isOpen: false }))}
        currentBalance={insufficientBalanceModal.currentBalance}
        requiredAmount={insufficientBalanceModal.requiredAmount}
        onAddFunds={() => {
          setInsufficientBalanceModal(prev => ({ ...prev, isOpen: false }));
          dispatch({ type: 'SET_VIEW', payload: 'billing' });
        }}
        onUpdatePaymentMethod={() => {
          setInsufficientBalanceModal(prev => ({ ...prev, isOpen: false }));
          dispatch({ type: 'SET_VIEW', payload: 'billing' });
        }}
        hasPaymentMethod={state.billingBalance?.has_payment_method || false}
      />
    </AppContext.Provider>
  );
}

export default AppProvider;
